import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { NumField } from "@/components/NumField";
import { api, errMsg } from "@/lib/api";
import { fmt, num, todayISO, isZero, when } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { EntryHistory } from "@/components/EntryHistory";

const s = (v) => (v === null || v === undefined ? "" : String(v));

const initial = (row, next) => row ? {
  date: row.date, deposit: s(row.deposit), withdrawal: s(row.withdrawal), cashout: s(row.cashout), cashout_note: row.cashout_note || "", cashout_to: row.cashout_to || "",
  bonus: s(row.bonus), overlimit: s(row.overlimit), available: row.available_entered ? s(row.available_bal) : "",
  diff: row.diff, note: row.note, reason: row.reason || "", is_settlement: row.is_settlement,
  op: s(row.op_bal), opManual: row.op_manual, autoOp: row.auto_op_bal,
  freeze: s(row.freeze_bal), freezeManual: row.freeze_manual, autoFreeze: row.auto_freeze,
} : {
  date: todayISO(), deposit: "", withdrawal: "", cashout: "", cashout_note: "", cashout_to: "", bonus: "", overlimit: "", available: "",
  diff: "", note: "", reason: "", is_settlement: false,
  op: s(next.op_bal), opManual: false, autoOp: next.op_bal,
  freeze: s(next.freeze_bal), freezeManual: false, autoFreeze: next.freeze_bal,
};

const Preview = ({ label, value, tone = "bg-white", testId }) => (
  <div className={`px-3 py-2 border border-slate-300 ${tone}`}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] opacity-70">{label}</p>
    <p className="font-mono text-base font-semibold tabular-nums" data-testid={testId}>{value}</p>
  </div>
);

export const EntryDrawer = ({ open, onOpenChange, row, next, bookId, sheet, onSaved }) => {
  const [f, setF] = useState(() => initial(null, next));
  const [busy, setBusy] = useState(false);
  const { can } = useAuth();
  const readOnly = Boolean(row) && !can("edit");
  const [payees, setPayees] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(initial(row, next));
    api.get("/payees").then((r) => setPayees(r.data)).catch(() => {});
  }, [open, row, next]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const total = num(f.op) + num(f.deposit) - num(f.withdrawal) - num(f.cashout);
  const active = total - num(f.freeze);
  const pnl = f.available === "" ? null : total - num(f.available);

  const save = async (e) => {
    e.preventDefault();
    if (f.available === "") return toast.error("Enter the actual Available Bal");
    const mismatch = !isZero(num(f.available) - total);
    if (mismatch && !f.reason.trim()) {
      document.querySelector('[data-testid="entry-reason-input"]')?.focus();
      return toast.error("Difference isn't zero — add a short reason before saving");
    }
    setBusy(true);
    const body = {
      date: f.date, deposit: num(f.deposit), withdrawal: num(f.withdrawal), cashout: num(f.cashout), cashout_note: f.cashout_note.trim(), cashout_to: f.cashout_to.trim(),
      bonus: num(f.bonus), overlimit: num(f.overlimit), available_bal: num(f.available),
      op_bal: f.opManual ? num(f.op) : null, freeze_bal: f.freezeManual ? num(f.freeze) : null,
      is_settlement: sheet === "sett" ? false : f.is_settlement, diff: f.diff.trim(), note: f.note.trim(), sheet,
      reason: mismatch ? f.reason.trim() : "",
    };
    try {
      row ? await api.put(`/entries/${row.id}`, body) : await api.post(`/books/${bookId}/entries`, body);
      toast.success(row ? "Entry updated" : "Entry added");
      onSaved();
      onOpenChange(false);
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/entries/${row.id}`);
      toast.success("Entry deleted");
      onSaved();
      onOpenChange(false);
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent className="rounded-none max-h-[94vh] max-w-2xl mx-auto bg-[#fbfaf6]" data-testid="entry-drawer">
        <DrawerHeader className="text-left pb-2">
          <DrawerTitle className="text-xl font-extrabold tracking-tight">{row ? "Edit entry" : "Add entry"}{sheet === "sett" && <span className="ml-2 align-middle text-[10px] font-bold tracking-[0.15em] bg-amber-200 text-amber-900 px-1.5 py-0.5">NEW SETT</span>}</DrawerTitle>
          <DrawerDescription>{readOnly ? "View only — you don't have permission to edit entries." : "Total, Active Bal and Difference are worked out for you."}</DrawerDescription>
          {row && (row.created_by_name || row.updated_by_name) && (
            <p className="text-[11px] text-slate-500 font-mono" data-testid="entry-audit">
              {row.created_by_name && <>Added by <b className="text-slate-800">{row.created_by_name}</b> {when(row.created_at)}</>}
              {row.updated_by_name && <> · Edited by <b className="text-slate-800">{row.updated_by_name}</b> {when(row.updated_at)}</>}
            </p>
          )}
        </DrawerHeader>
        <form onSubmit={save} className="overflow-y-auto px-4 pb-6">
          <fieldset disabled={readOnly} className="space-y-4 min-w-0">
          <div className="grid grid-cols-2 gap-3 items-end">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Date</span>
              <input type="date" required data-testid="entry-date-input" value={f.date} onChange={(e) => set("date")(e.target.value)}
                className="mt-1.5 w-full h-12 px-3 border border-slate-300 bg-white outline-none focus:border-slate-900 font-mono" />
            </label>
            {sheet !== "sett" && <label className={`h-12 flex items-center justify-between gap-2 px-3 border cursor-pointer transition-colors ${f.is_settlement ? "bg-amber-100 border-amber-500" : "bg-white border-slate-300"}`}>
              <span className="text-xs font-bold tracking-[0.12em] uppercase">Settlement</span>
              <Switch data-testid="entry-settlement-switch" checked={f.is_settlement} onCheckedChange={set("is_settlement")} />
            </label>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Op Bal" value={f.op} testId="entry-opbal-input" hint="auto"
              onChange={(v) => setF((p) => ({ ...p, op: v, opManual: true }))}
              onReset={f.opManual ? () => setF((p) => ({ ...p, op: s(p.autoOp), opManual: false })) : null} />
            <NumField label="Freeze Bal" value={f.freeze} testId="entry-freeze-input" hint="carried"
              onChange={(v) => setF((p) => ({ ...p, freeze: v, freezeManual: true }))}
              onReset={f.freezeManual ? () => setF((p) => ({ ...p, freeze: s(p.autoFreeze), freezeManual: false })) : null} />
            <NumField label="Deposit" value={f.deposit} onChange={set("deposit")} testId="entry-deposit-input" />
            <NumField label="Withdrawal" value={f.withdrawal} onChange={set("withdrawal")} testId="entry-withdrawal-input" />
            <NumField label="Cashout" value={f.cashout} onChange={set("cashout")} testId="entry-cashout-input" hint="− adds back" />
            <label className="block col-span-2 -mt-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Paid to</span>
              <input data-testid="entry-cashout-to-input" list="payee-list" value={f.cashout_to} maxLength={80} onChange={(e) => set("cashout_to")(e.target.value)}
                placeholder="Who received the cash"
                className="mt-1.5 w-full h-11 px-3 border border-orange-300 bg-orange-50/50 outline-none focus:border-slate-900 font-semibold" />
              <datalist id="payee-list">{payees.map((p) => <option key={p} value={p} />)}</datalist>
            </label>
            <label className="block col-span-2 -mt-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Cashout description</span>
              <input data-testid="entry-cashout-note-input" value={f.cashout_note} maxLength={200} onChange={(e) => set("cashout_note")(e.target.value)}
                placeholder="e.g. paid to Ravi via UPI"
                className="mt-1.5 w-full h-11 px-3 border border-orange-300 bg-orange-50/50 outline-none focus:border-slate-900" />
            </label>
            <NumField label="Bonus" value={f.bonus} onChange={set("bonus")} testId="entry-bonus-input" />
            <NumField label="Overlimit" value={f.overlimit} onChange={set("overlimit")} testId="entry-overlimit-input" />
            <NumField label="Available Bal" value={f.available} onChange={set("available")} testId="entry-available-input"
              hint="actual" accent="ring-2 ring-cyan-300" placeholder="Actual" />
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="entry-preview">
            <Preview label="Total Bal" value={fmt(total)} tone="bg-yellow-100" testId="preview-total" />
            <Preview label="Active Bal" value={fmt(active)} testId="preview-active" />
            <Preview label="Difference" value={pnl === null ? "—" : fmt(pnl)} testId="preview-pnl"
              tone={pnl === null ? "bg-white" : isZero(pnl) ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"} />
          </div>
          {pnl !== null && !isZero(pnl) && (
            <label className="block fade-up" data-testid="entry-reason-field">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-red-700">Reason for Difference {fmt(pnl)} · required</span>
              <input data-testid="entry-reason-input" value={f.reason} maxLength={200} onChange={(e) => set("reason")(e.target.value)}
                placeholder="e.g. bonus not credited yet"
                className="mt-1.5 w-full h-12 px-3 border-2 border-red-400 bg-red-50 outline-none focus:border-red-700" />
            </label>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-3">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Diff</span>
              <input data-testid="entry-diff-input" value={f.diff} onChange={(e) => set("diff")(e.target.value)}
                className="mt-1.5 w-full h-12 px-3 border border-slate-300 bg-white outline-none focus:border-slate-900 font-mono" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Note</span>
              <input data-testid="entry-note-input" value={f.note} onChange={(e) => set("note")(e.target.value)} placeholder="e.g. NO SS 500"
                className="mt-1.5 w-full h-12 px-3 border border-slate-300 bg-white outline-none focus:border-slate-900" />
            </label>
          </div>
          </fieldset>
          <div className="flex gap-2 pt-5">
            {row && can("delete") && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button type="button" data-testid="delete-entry-btn" className="h-12 w-12 shrink-0 grid place-items-center border border-red-300 text-red-700 hover:bg-red-50">
                    <Trash2 size={18} />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                    <AlertDialogDescription>Every balance after it will be recalculated.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none" data-testid="delete-entry-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction className="rounded-none bg-red-700 hover:bg-red-800" onClick={remove} data-testid="delete-entry-confirm">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {!readOnly && <button data-testid="save-entry-btn" disabled={busy}
              className="flex-1 h-12 bg-slate-900 text-white font-semibold hover:bg-slate-800 active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60">
              {busy ? "Saving…" : row ? "Save changes" : "Save entry"}
            </button>}
          </div>
          {row && <EntryHistory key={row.id} entryId={row.id} />}
        </form>
      </DrawerContent>
    </Drawer>
  );
};
